import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  createRecordView,
  deleteRecordView,
  getRecordViews,
  setDefaultRecordView,
  updateRecordView,
} from '@/services/api/record-view.service'
import type { RecordViewRequest } from '@/types/api/record-view'

import { useWorkspace } from './useWorkspace'

export function useRecordViews(recordGroupId: string | undefined) {
  const { currentWorkspaceId } = useWorkspace()
  const queryClient = useQueryClient()
  const groupKey = ['workspace', currentWorkspaceId, 'record-groups']
  const viewsKey = [...groupKey, recordGroupId, 'views']
  // The group's defaultViewId lives on the group detail, so it is refreshed with the views.
  const invalidate = () => queryClient.invalidateQueries({ queryKey: groupKey })

  const query = useQuery({
    queryKey: viewsKey,
    queryFn: () => getRecordViews(recordGroupId!),
    enabled: !!recordGroupId && !!currentWorkspaceId,
    staleTime: 30 * 1000,
  })

  const create = useMutation({
    mutationFn: (data: RecordViewRequest) => createRecordView(recordGroupId!, data),
    onSuccess: invalidate,
  })
  const update = useMutation({
    mutationFn: ({ id, data }: { id: string; data: RecordViewRequest }) => updateRecordView(id, data),
    onSuccess: invalidate,
  })
  const remove = useMutation({ mutationFn: deleteRecordView, onSuccess: invalidate })
  const setDefault = useMutation({
    mutationFn: (viewId: string | null) => setDefaultRecordView(recordGroupId!, viewId),
    onSuccess: invalidate,
  })

  return {
    views: query.data ?? [],
    isLoading: query.isLoading,
    create: create.mutateAsync,
    update: update.mutateAsync,
    remove: remove.mutateAsync,
    setDefault: setDefault.mutateAsync,
    isSaving: create.isPending || update.isPending || remove.isPending || setDefault.isPending,
  }
}
